/**
 * @file api.ts
 * @brief TanStack Query hooks for company payment methods (Stripe).
 *
 * @author DES
 * @date 19-06-2026
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { billingEndpoints, companyEndpoints } from "@/shared/api/endpoints";
import { z } from "zod";
import {
  apiDelete,
  apiGet,
  apiPost,
  apiPut,
} from "@/shared/api/client";
import {
  CompanyPaymentMethodsResponseSchema,
  CompanyProfileResponseSchema,
  SetupIntentResponseSchema,
  type CompanyBillingProfile,
  type CompanyPaymentMethodsResponse,
  type PaymentMethodCard,
  type UpdateCompanyBillingProfileInput,
} from "./schemas";

export const companyBillingKeys = {
  all: ["company", "billing"] as const,
  profile: () => ["company", "billing", "profile"] as const,
  paymentMethods: (companyId: string) =>
    ["company", "billing", "payment-methods", companyId] as const,
};

function companyQuery(companyId: string): string {
  return `company_id=${encodeURIComponent(companyId)}`;
}

export function useCompanyBillingProfile() {
  return useQuery<CompanyBillingProfile>({
    queryKey: companyBillingKeys.profile(),
    queryFn: async () => {
      const raw = await apiGet<unknown>(companyEndpoints.profile);
      return CompanyProfileResponseSchema.parse(raw).company;
    },
  });
}

export function useUpdateCompanyBillingProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateCompanyBillingProfileInput) => {
      const raw = await apiPut<unknown>(companyEndpoints.profile, input);
      return CompanyProfileResponseSchema.parse(raw).company;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: companyBillingKeys.profile() });
    },
  });
}

export function useCompanyPaymentMethods(companyId: string | null) {
  return useQuery<CompanyPaymentMethodsResponse>({
    queryKey: companyBillingKeys.paymentMethods(companyId ?? ""),
    enabled: Boolean(companyId),
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        `${billingEndpoints.customer}?${companyQuery(companyId!)}`,
      );
      return CompanyPaymentMethodsResponseSchema.parse(raw);
    },
  });
}

export function useCreateCompanySetupIntent() {
  return useMutation({
    mutationFn: async (companyId: string) => {
      const raw = await apiPost<unknown>(
        `${billingEndpoints.setupIntent}?${companyQuery(companyId)}`,
        {},
      );
      return SetupIntentResponseSchema.parse(raw);
    },
  });
}

/** A SetupIntent for a company card already saved: "Confirm with your bank" (BACKEND f5e603e). */
export async function createCompanyConfirmIntent(companyId: string, paymentMethodId: string): Promise<string> {
  const raw = await apiPost<unknown>(`${billingEndpoints.setupIntent}?${companyQuery(companyId)}`, {
    paymentMethodId,
  });
  return SetupIntentResponseSchema.parse(raw).clientSecret;
}

export function useSetCompanyDefaultPaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      companyId,
      paymentMethodId,
    }: {
      companyId: string;
      paymentMethodId: string;
    }) => {
      await apiPost<unknown>(
        `${billingEndpoints.paymentMethodDefault(paymentMethodId)}?${companyQuery(companyId)}`,
        {},
      );
    },
    onSuccess: (_data, { companyId }) => {
      void qc.invalidateQueries({
        queryKey: companyBillingKeys.paymentMethods(companyId),
      });
    },
  });
}

export function useDeleteCompanyPaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      companyId,
      paymentMethodId,
    }: {
      companyId: string;
      paymentMethodId: string;
    }) => {
      const q = companyQuery(companyId);
      try {
        await apiPost<unknown>(
          `${billingEndpoints.paymentMethodDelete(paymentMethodId)}?${q}`,
          {},
        );
      } catch {
        await apiDelete<unknown>(
          `${billingEndpoints.paymentMethodById(paymentMethodId)}?${q}`,
        );
      }
    },
    onSuccess: (_data, { companyId }) => {
      void qc.invalidateQueries({
        queryKey: companyBillingKeys.paymentMethods(companyId),
      });
    },
  });
}

/** Cards with `isDefault` flag derived from the API response. */
export function mapPaymentMethods(
  data: CompanyPaymentMethodsResponse | undefined,
): Array<PaymentMethodCard & { isDefault: boolean }> {
  if (!data) return [];
  const defaultId = data.defaultPaymentMethod;
  return data.cards
    .map((c) => ({ ...c, isDefault: c.id === defaultId }))
    .sort((a, b) => {
      if (a.isDefault === b.isDefault) return 0;
      return a.isDefault ? -1 : 1;
    });
}


// ---------------------------------------------------------------- plan (21-10-2026)

const CompanySubscribeSchema = z
  .object({
    subscriptionId: z.string(),
    clientSecret: z.string().nullable().optional(),
    requiresAction: z.boolean().optional(),
  })
  .passthrough();

/**
 * Buy Business for the company. The server knows a company admin and makes a
 * per-seat plan (quantity = active members, never below the minimum). A
 * `clientSecret` means the bank wants 3DS on the first payment.
 */
export async function subscribeCompanyBusiness(input: { interval: "month" | "year"; paymentMethodId: string }) {
  return CompanySubscribeSchema.parse(
    await apiPost<unknown>(billingEndpoints.subscriptions, { planCode: "business", ...input }),
  );
}

/** Re-read the company plan from Stripe through the server. */
export async function syncCompanyPlan(subscriptionId: string): Promise<void> {
  await apiPost<unknown>(billingEndpoints.subscriptionsSync, { subscriptionId });
}

/** Cancel at the end of the paid period (the company keeps it until then). */
export async function cancelCompanyPlan(): Promise<void> {
  await apiPost<unknown>(billingEndpoints.subscriptionsCancel, { atPeriodEnd: true });
}

/** Undo a cancel before the period ends. */
export async function resumeCompanyPlan(): Promise<void> {
  await apiPost<unknown>(billingEndpoints.subscriptionsResume, {});
}

const CompanyChangePreviewSchema = z
  .object({
    seats: z.number().nullable().optional(),
    currency: z.string(),
    amountDueCents: z.number(),
    creditCents: z.number().nullable().optional(),
  })
  .passthrough();
export type CompanyChangePreview = z.infer<typeof CompanyChangePreviewSchema>;

/** What switching the company plan's interval costs today: Stripe's upcoming invoice (ADR-049). */
export async function previewCompanyPlanChange(interval: "month" | "year"): Promise<CompanyChangePreview> {
  const usp = new URLSearchParams({ planCode: "business", interval });
  return CompanyChangePreviewSchema.parse(
    await apiGet<unknown>(`${billingEndpoints.subscriptionsChangePreview}?${usp}`),
  );
}

const CompanyChangeSchema = z
  .object({
    clientSecret: z.string().nullable().optional(),
    pending: z.boolean().optional(),
    subscriptionId: z.string().optional(),
  })
  .passthrough();

/** Switch Business monthly <-> yearly; a `clientSecret` means the bank must confirm the charge. */
export async function changeCompanyPlan(interval: "month" | "year") {
  return CompanyChangeSchema.parse(
    await apiPost<unknown>(billingEndpoints.subscriptionsChange, { planCode: "business", interval }),
  );
}
